import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { CheckedListControl } from "@/Common/Components/Inputs/CheckedListControl";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { SearchSettingsFooter } from "@/Common/Components/Settings/SearchSettingsFooter";
import { EmployeeHelper } from "@/Common/Helpers/EmployeeHelper";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { CliniciansCheckboxControl_OptimizedV2 } from "@/Communications/Components/Inputs/CliniciansCheckboxControl_OptimizedV2";
import { CommunicationsHelper } from "@/Communications/Helpers/CommunicationsHelper";
import { RecallSearchSettings } from "@/Communications/Models/Settings/RecallSearchSettings";
import { UserSettings } from "@/Globals/Models/UserSettings";
import { BaseCommunicationSettingsState } from "./BaseCommunicationSettingsState";

import "./RecallSearchSettingsContainer_OptimizedV2.css";

interface RecallSearchSettingsContainer_OptimizedV2Props {
    id?: string;
    className?: string;
    style?: any;

    onSaveSettingsCallback?: (model: RecallSearchSettingsContainer_OptimizedV2State) => void;
    onCancelClick?: () => void;
    onSettingsButtonClick: () => void;

    savedState?: () => RecallSearchSettingsContainer_OptimizedV2State;
}

class RecallSearchSettingsContainer_OptimizedV2State extends BaseCommunicationSettingsState {
    public isSaveSettingChecked: boolean;
    public isAllDeactivatedCliniciansChecked: boolean;
    public isAllStatusChecked: boolean;
    public statuses: CheckboxUIModel[];

    public static getDefault(includeUserPreferences: boolean = true): RecallSearchSettingsContainer_OptimizedV2State {
        const state = new RecallSearchSettingsContainer_OptimizedV2State();

        state.setBaseDefault([sessionStore.individualContactId]);
        state.priorities = CommunicationsHelper.GetCommunicationPrioritiesCheckboxesFromCache("PriorityType", CheckboxTarget.Myle_Communications_Recalls_Search_Priority);
        state.isAllStatusChecked = false;
        state.isAllDeactivatedCliniciansChecked = false;
        state.statuses = CommunicationsHelper.GetRecallStatusesFromCache();
        if (includeUserPreferences)
            state.setSearchSettingsFromUserPreferences();

        return state;
    }

    public setSearchSettingsFromUserPreferences(): void {
        const setting = cacheStore.UserSettings.RecallSearchSettings;
        if (setting == null)
            return;

        this.setBaseSearchSettingsFromUserPreferences(setting);

        this.isAllDeactivatedCliniciansChecked = setting.IsDeactivatedChecked || this.isAllDeactivatedCliniciansChecked;
        this.isAllStatusChecked = setting.IsAllStatusChecked || this.isAllStatusChecked;

        if (setting.Status)
            this.statuses.mutate(c => c.Checked = setting.Status.contains(c.Id));
    }
}

class RecallSearchSettingsContainer_OptimizedV2 extends BaseReactComponent<RecallSearchSettingsContainer_OptimizedV2Props, RecallSearchSettingsContainer_OptimizedV2State> {
    public constructor(props: RecallSearchSettingsContainer_OptimizedV2Props) {
        super(props);
        this.state = RecallSearchSettingsContainer_OptimizedV2State.getDefault();
    }

    public componentDidMount(): void {
        const rawSaved = this.props.savedState?.();
        if (rawSaved) {
            this.setState({
                ...rawSaved,
                isSaveSettingChecked: false
            });
        }
        super.setOverlay("RecallSearchSettingsContainer_OptimizedV2", { transparent: true, onClicked: () => this.onSaveSettings() });
    }

    public onSaveSettings(): void {
        communicationsStore.communicationsTabSavedState.recallSettingContainerSavedState = this.state;

        if (this.state.isSaveSettingChecked)
            this.saveUserSettings();

        this.props.onSaveSettingsCallback(this.state);
    }

    public render(): JSX.Element {
        return (
            <div className="filter-component filter-component-responsive" onClick={e => e && e.stopPropagation()}>
                <table className="f-content">
                    <thead>
                        <tr>
                            <th className="first">
                                <label style={{ display: "inline" }}>{cacheStore.GetString("DueDateLabel").toUpperCase()}</label>
                            </th>
                            {this.state.sites && this.state.sites.length > 1 && (
                                <th>
                                    <CheckboxControl
                                        id="sites_check"
                                        label={cacheStore.GetString("SitesLabel").toUpperCase()}
                                        checked={this.state.isAllSitesChecked}
                                        onChange={() => this.mutateState(
                                            s => {
                                                s.isAllSitesChecked = !s.isAllSitesChecked;
                                                s.sites.mutate(site => site.Checked = s.isAllSitesChecked);
                                            },
                                            () => this.handleSitesChanged()
                                        )}
                                    />
                                </th>
                            )}
                            <th>
                                <CliniciansCheckboxControl_OptimizedV2
                                    checkedClinicians={this.state.isAllCliniciansChecked}
                                    checkedDeactivatedClinicians={this.state.isAllDeactivatedCliniciansChecked}
                                    onCheckClinicians={() => this.mutateState(s => {
                                        s.isAllCliniciansChecked = !s.isAllCliniciansChecked;
                                        s.clinicians.mutate(c => c.Checked = s.isAllCliniciansChecked);
                                    })}
                                    onCheckDeactivatedClinicians={() =>
                                        this.mutateState(s => s.isAllDeactivatedCliniciansChecked = !s.isAllDeactivatedCliniciansChecked)
                                    }
                                />
                            </th>
                            <th>
                                <CheckboxControl
                                    id="urgency_check"
                                    label={cacheStore.GetString("PriorityLabel").toUpperCase()}
                                    checked={this.state.isAllPrioritiesChecked}
                                    onChange={() => this.mutateState(s => {
                                        s.isAllPrioritiesChecked = !s.isAllPrioritiesChecked;
                                        s.priorities.mutate(p => p.Checked = s.isAllPrioritiesChecked);
                                    })}
                                />
                            </th>
                            <th>
                                <CheckboxControl
                                    id="status_check"
                                    label={cacheStore.GetString("StatusDLabel").toUpperCase()}
                                    checked={this.state.isAllStatusChecked}
                                    onChange={() => this.mutateState(s => {
                                        s.isAllStatusChecked = !s.isAllStatusChecked;
                                        s.statuses.mutate(status => status.Checked = s.isAllStatusChecked);
                                    })}
                                />
                            </th>
                            <th className="last recall-search-settings-sort-by-container">
                                <label className="uppercase">{cacheStore.GetString("SortByLabel")}</label>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td className="first">
                                <div style={{ width: "237px" }}>
                                    <MultiDateSelector
                                        id="message_dates"
                                        dataCy="recallSearchSettings-date"
                                        from={this.state.dateFrom}
                                        fromChange={date => this.mutateState(s => s.dateFrom = date && moment(date).startOf("day").toDate())}
                                        fromSetTodayLabel={cacheStore.GetString("TodayLabel")}
                                        to={this.state.dateTo}
                                        toChange={date => this.mutateState(s => s.dateTo = date && moment(date).endOf("day").toDate())}
                                        toSetTodayLabel={cacheStore.GetString("TodayLabel")}
                                    />
                                </div>
                            </td>
                            {this.state.sites && this.state.sites.length > 1 && (
                                <td className="sites-column">
                                    <div style={{ maxHeight: "400px", minHeight: "400px", overflow: "auto" }}>
                                        <CheckedListControl
                                            DataSource={this.state.sites.toImmutable()}
                                            onChange={(isChecked, id) => this.mutateState(
                                                s => s.sites.mutate(site => site.Checked = isChecked, site => site.Id === id),
                                                () => this.handleSitesChanged()
                                            )}
                                            MaxTextLength={25}
                                        />
                                    </div>
                                </td>
                            )}
                            <td style={{ width: "227px" }}>
                                <input
                                    className="form-control"
                                    type="text"
                                    value={this.state.cliniciansSearchText}
                                    onChange={(e: any) => this.handleCliniciansFilterTextChanged(e.target.value)}
                                    placeholder={cacheStore.GetString("SearchLabel").toUpperCase()}
                                />
                                <div style={{ maxHeight: "400px", minHeight: "400px", overflow: cacheStore.ClinicSettings.EnableLazyRenderingFilterSettingsClinicians ? "hidden" : "auto" }}>
                                    <CheckedListControl
                                        DataSource={this.getFilteredClinicians().toImmutable()}
                                        onChange={(isChecked, id) => this.mutateState(s => {
                                            s.clinicians.mutate(c => c.Checked = isChecked, c => c.Id === id);
                                            s.employeesGlobalCache = s.employeesGlobalCache.mutate(c => c.Checked = isChecked, c => c.Id === id);
                                        })}
                                        sort
                                        enableLazyRendering
                                    />
                                </div>
                            </td>
                            <td>
                                <CheckedListControl
                                    DataSource={this.state.priorities.toImmutable()}
                                    idModifier="PriorityRecall"
                                    onChange={(isChecked, id) =>
                                        this.mutateState(s => s.priorities.mutate(p => p.Checked = isChecked, p => p.Id === id))
                                    }
                                />
                            </td>
                            <td>
                                <CheckedListControl
                                    DataSource={this.state.statuses.toImmutable()}
                                    idModifier="StatusRecall"
                                    onChange={(isChecked, id) =>
                                        this.mutateState(s => s.statuses.mutate(status => status.Checked = isChecked, status => status.Id === id))
                                    }
                                />
                            </td>
                            <td className="last recall-search-settings-sort-by-container">
                                <div>
                                    <div onClick={() => this.mutateState(s => s.orderByPriorityFirst = true)}>
                                        <label>
                                            <input type="radio" checked={this.state.orderByPriorityFirst} />
                                            <label style={{ paddingLeft: "5px" }}>{cacheStore.GetString("PriorityHighToLowLabel")}</label>
                                        </label>
                                    </div>
                                    <div onClick={() => this.mutateState(s => s.orderByPriorityFirst = false)}>
                                        <label>
                                            <input type="radio" checked={!this.state.orderByPriorityFirst} />
                                            <label className="captilize-first-letter" style={{ paddingLeft: "5px" }}>{cacheStore.GetString("DueDateLabel").toLocaleLowerCase()}</label>
                                        </label>
                                    </div>

                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <SearchSettingsFooter
                    footerClassName="recall-footer-responsive"
                    onClose={() => this.props.onCancelClick()}
                    onOk={() => this.onSaveSettings()}
                    onColumnOrderingButtonClick={() => this.props.onSettingsButtonClick()}
                    onResultsPerPageChange={size => this.mutateState(s => s.selectedPageSizeOption = size)}
                    selectedPageSizeOption={this.state.selectedPageSizeOption}
                    pageSizeOptions={this.state.pageSizeOptions}
                    isSaveSettingsChecked={this.state.isSaveSettingChecked}
                    onSaveSettingsCheckboxChange={value => this.mutateState(s => s.isSaveSettingChecked = value)}
                />
            </div>
        );
    }

    private handleCliniciansFilterTextChanged(text: string): void {
        this.mutateState(s => s.cliniciansSearchText = text);
    }

    private handleSitesChanged(): void {
        this.mutateState(s =>
            s.clinicians = EmployeeHelper
                .filterCliniciansByCheckedSites(s.employeesGlobalCache, s.sites)
        );
    }

    private getFilteredClinicians(): EmployeeUIModel[] {
        return EmployeeHelper
            .filterCliniciansBySearchText(this.state.clinicians, this.state.cliniciansSearchText, this.state.isAllDeactivatedCliniciansChecked);
    }

    private saveUserSettings(): void {
        const userSettings = new UserSettings();
        userSettings.RecallSearchSettings = RecallSearchSettings.FromModel(this.state);

        Client.Post(
            ServerOperations.Server.Myle_UpdateUserSettings,
            {
                UserSettings: userSettings,
                UserId: sessionStore.userId
            },
            () => { }
        );
        cacheStore.UserSettings.RecallSearchSettings = userSettings.RecallSearchSettings;
    }
}

export { RecallSearchSettingsContainer_OptimizedV2, RecallSearchSettingsContainer_OptimizedV2State };
